import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheckCircle, FiClock, FiMessageCircle, FiPackage, FiSend, FiTruck, FiX, FiXCircle } from "react-icons/fi";
import { api, API_ENDPOINTS } from "../../config/api";
import { keepPreviousIfSame } from "../../utils/state";

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [restaurantId, setRestaurantId] = useState(null);
  const userId = localStorage.getItem("userId");
  const location = useLocation();
  const navigate = useNavigate();

  const fetchRestaurantId = useCallback(async () => {
    try {
      console.log("Fetching restaurant ID for user:", userId);
      const data = await api.get(API_ENDPOINTS.RESTAURANT_BY_OWNER(userId));
      console.log("Fetched restaurant data:", data);
      if (data && data.id) {
        setRestaurantId(data.id);
        console.log("Set restaurant ID to:", data.id);
      } else {
        console.error("No restaurant ID found in response:", data);
        setError("Restaurant information not found");
      }
    } catch (err) {
      console.error("Error fetching restaurant ID:", err);
      setError("Failed to fetch restaurant information: " + err.message);
    }
  }, [userId]);

  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    if (!restaurantId) {
      console.log("No restaurant ID available, skipping order fetch");
      return;
    }
    
    if (!silent) setError("");
    try {
      console.log("Fetching orders for restaurant ID:", restaurantId);
      const data = await api.get(API_ENDPOINTS.RESTAURANT_ORDERS(restaurantId));
      console.log("Fetched orders:", data);
      const nextOrders = Array.isArray(data) ? data : [];
      setOrders(previous => keepPreviousIfSame(previous, nextOrders));
      if (!silent) setError("");
    } catch (err) {
      console.error("Error fetching orders:", err);
      if (!silent) setError(err.message);
    }
  }, [restaurantId]);

  const updateOrderStatus = async (orderId, newStatus) => {
    if (!restaurantId) {
      setError("Restaurant ID not available");
      return;
    }
    
    try {
      console.log("=== Frontend Order Status Update Debug ===");
      console.log("Order ID:", orderId);
      console.log("New Status:", newStatus);
      console.log("Restaurant ID:", restaurantId);
      console.log("User ID:", userId);
      const requestData = { status: newStatus };
      console.log("Request Data:", requestData);
      
      console.log("Updating order status:", { orderId, newStatus, restaurantId });
      const data = await api.put(API_ENDPOINTS.ORDER_STATUS(orderId), requestData);
      console.log("Updated order status response:", data);
      
      // Update the order in the local state immediately
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus }
            : order
        )
      );
      
      // Also refresh the orders list
      await fetchOrders();
    } catch (err) {
      console.error("Error updating order status:", err);
      console.error("Error details:", {
        message: err.message,
        status: err.status,
        response: err.response
      });
      setError("Failed to update order status: " + err.message);
    }
  };

  const fetchChatMessages = useCallback(async (orderId) => {
    try {
      const data = await api.get(`${API_ENDPOINTS.SUPPORT_MESSAGES}?orderId=${orderId}&restaurantId=${restaurantId}`);
      const nextMessages = Array.isArray(data) ? data : [];
      setChatMessages(previous => keepPreviousIfSame(previous, nextMessages));
    } catch (err) {
      console.error("Error fetching chat messages:", err);
      setChatMessages(previous => previous.length === 0 ? previous : []);
    }
  }, [restaurantId]);

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedOrder) return;
    
    setSendingMessage(true);
    try {
      const messageData = {
        orderId: selectedOrder.id,
        restaurantId: restaurantId,
        customerId: selectedOrder.userId,
        sender: "restaurant",
        message: newMessage
      };
      
      const data = await api.post(API_ENDPOINTS.SUPPORT_MESSAGES, messageData);
      setChatMessages(prev => [...prev, data]);
      setNewMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Failed to send message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  const openChatModal = useCallback(async (order) => {
    setSelectedOrder(order);
    setShowChatModal(true);
    await fetchChatMessages(order.id);
    
    // Mark all messages as read when opening chat
    try {
      await api.put(API_ENDPOINTS.SUPPORT_MARK_ALL_READ, null, {
        params: {
          restaurantId: restaurantId,
          orderId: order.id
        }
      });
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
    window.dispatchEvent(new CustomEvent("foodlyNotificationsChanged"));
  }, [fetchChatMessages, restaurantId]);

  const closeChatModal = () => {
    setShowChatModal(false);
    setSelectedOrder(null);
    setChatMessages([]);
    setNewMessage("");
    if (new URLSearchParams(location.search).has("orderId")) {
      navigate("/restaurant/orders", { replace: true });
    }
  };

  // Initial fetch
  useEffect(() => {
    async function initialFetch() {
      setLoading(true);
      await fetchRestaurantId();
      setLoading(false);
    }
    if (userId) {
      initialFetch();
    }
  }, [userId, fetchRestaurantId]);

  // Fetch orders when restaurant ID is available
  useEffect(() => {
    if (restaurantId) {
      fetchOrders();
    }
  }, [restaurantId, fetchOrders]);

  useEffect(() => {
    const orderIdParam = new URLSearchParams(location.search).get("orderId");
    if (!orderIdParam || !restaurantId || orders.length === 0) return;

    const targetOrderId = Number(orderIdParam);
    if (!Number.isFinite(targetOrderId)) return;
    if (showChatModal && Number(selectedOrder?.id) === targetOrderId) return;

    const targetOrder = orders.find(order => Number(order.id) === targetOrderId);
    if (targetOrder) {
      setSelectedStatus("all");
      openChatModal(targetOrder);
    }
  }, [location.search, openChatModal, orders, restaurantId, selectedOrder, showChatModal]);

  // Polling for new orders
  useEffect(() => {
    if (!restaurantId) return;

    const pollInterval = setInterval(() => {
      fetchOrders({ silent: true });
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [restaurantId, fetchOrders]);

  // Polling for new chat messages when chat modal is open
  useEffect(() => {
    if (!showChatModal || !selectedOrder) return;

    const chatPollInterval = setInterval(() => {
      fetchChatMessages(selectedOrder.id);
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(chatPollInterval);
  }, [fetchChatMessages, showChatModal, selectedOrder]);

  if (loading) return <div className="p-10 text-center">Loading...</div>;
  if (error) return <div className="p-10 text-center text-red-600">{error}</div>;

  // Filter orders by status
  const filteredOrders = selectedStatus === "all" 
    ? orders 
    : orders.filter(order => order.status === selectedStatus);

  // Sort orders by date (newest first) and status
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    // First sort by status priority
    const statusPriority = {
      "New": 0,
      "Accepted": 1,
      "Preparing": 2,
      "Out for Delivery": 3,
      "Delivered": 4,
      "Cancelled": 5
    };
    const statusDiff = statusPriority[a.status] - statusPriority[b.status];
    if (statusDiff !== 0) return statusDiff;
    
    // Then sort by date (newest first)
    const dateA = new Date(a.date || a.createdAt);
    const dateB = new Date(b.date || b.createdAt);
    return dateB - dateA;
  });

  const getStatusColor = (status) => {
    // Normalize status to handle both "NEW" and "New" cases
    const normalizedStatus = status?.toLowerCase();
    
    switch (normalizedStatus) {
      case "new": return "bg-blue-100 text-blue-800";
      case "accepted": return "bg-yellow-100 text-yellow-800";
      case "preparing": return "bg-orange-100 text-orange-800";
      case "out for delivery": return "bg-purple-100 text-purple-800";
      case "delivered": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getNextStatus = (currentStatus) => {
    // Normalize status to handle both "NEW" and "New" cases
    const normalizedStatus = currentStatus?.toLowerCase();
    
    switch (normalizedStatus) {
      case "new": return "Accepted";
      case "accepted": return "Preparing";
      case "preparing": return "Out for Delivery";
      case "out for delivery": return "Delivered";
      default: return null;
    }
  };

  const getStatusIcon = (status) => {
    // Normalize status to handle both "NEW" and "New" cases
    const normalizedStatus = status?.toLowerCase();
    
    switch (normalizedStatus) {
      case "new": return FiPackage;
      case "accepted": return FiCheckCircle;
      case "preparing": return FiClock;
      case "out for delivery": return FiTruck;
      case "delivered": return FiCheckCircle;
      case "cancelled": return FiXCircle;
      default: return FiPackage;
    }
  };

  return (
    <div className="app-page surface-panel">
      <h2 className="text-2xl font-bold mb-6 text-[#16213e]">Restaurant Orders</h2>
      
      {/* Debug Information */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">Debug Info</h3>
          <div className="text-sm text-yellow-700 space-y-1">
            <div>User ID: {userId}</div>
            <div>Restaurant ID: {restaurantId || 'Not loaded'}</div>
            <div>Orders Count: {orders.length}</div>
            <div>Loading: {loading ? 'Yes' : 'No'}</div>
            <div>Error: {error || 'None'}</div>
            <div>Chat Modal Open: {showChatModal ? 'Yes' : 'No'}</div>
            <div>Selected Order: {selectedOrder?.id || 'None'}</div>
            <div>Sample Order Status: {orders[0]?.status || 'No orders'}</div>
            <div>Next Status for New: {getNextStatus('New')}</div>
            <div>Next Status for NEW: {getNextStatus('NEW')}</div>
          </div>
        </div>
      )}
      
      {/* Status Filter */}
      <div className="mb-6">
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Orders</option>
          <option value="New">New</option>
          <option value="Accepted">Accepted</option>
          <option value="Preparing">Preparing</option>
          <option value="Out for Delivery">Out for Delivery</option>
          <option value="Delivered">Delivered</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      {sortedOrders.length === 0 ? (
        <div className="text-gray-500 text-center py-8">No orders found.</div>
      ) : (
        <div className="space-y-4">
          {sortedOrders.map((order) => (
            <div key={order.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700 ring-1 ring-primary-100">
                    {React.createElement(getStatusIcon(order.status), { className: "h-5 w-5" })}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#16213e]">Order #{order.id}</h3>
                    <p className="text-gray-600 text-sm">
                      {new Date(order.date || order.createdAt).toLocaleString()}
                    </p>
                    <p className="text-gray-600 text-sm">Customer: {order.customerName || "Unknown"}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-[#16213e] mb-2">Items:</h4>
                <ul className="space-y-1">
                  {order.items && order.items.map((item, index) => (
                                          <li key={index} className="text-sm text-gray-600">
                        {item.quantity}x {item.name} - ₹{item.price}
                      </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div className="text-lg font-semibold text-[#16213e]">
                  Total: ₹{order.total || order.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    onClick={() => openChatModal(order)}
                    className="btn btn-secondary min-h-[36px] px-3 py-2 text-sm"
                  >
                    <FiMessageCircle className="h-4 w-4" />
                    Chat
                  </button>
                  {getNextStatus(order.status) && (
                    <button
                      onClick={() => updateOrderStatus(order.id, getNextStatus(order.status))}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Mark as {getNextStatus(order.status)}
                    </button>
                  )}
                  {order.status?.toLowerCase() === "new" && (
                    <button
                      onClick={() => updateOrderStatus(order.id, "Cancelled")}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      Cancel Order
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/50 p-4 backdrop-blur-sm">
          <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg border border-neutral-200 bg-white shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-neutral-200 p-6">
              <div>
                <h3 className="text-lg font-semibold text-[#16213e]">
                  Chat - Order #{selectedOrder.id}
                </h3>
                <p className="text-sm text-gray-600">
                  Customer: {selectedOrder.customerName || "Unknown"}
                </p>
              </div>
              <button
                onClick={closeChatModal}
                className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="max-h-96 flex-1 space-y-4 overflow-y-auto bg-neutral-50 p-6">
              {chatMessages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No messages yet. Start the conversation!
                </div>
              ) : (
                chatMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === "restaurant" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-xs rounded-md px-4 py-2 ${
                        message.sender === "restaurant"
                          ? "bg-primary-600 text-white"
                          : "border border-neutral-200 bg-white text-neutral-800"
                      }`}
                    >
                      <p className="text-sm">{message.message}</p>
                      <p className={`text-xs mt-1 ${
                        message.sender === "restaurant" ? "text-primary-100" : "text-neutral-500"
                      }`}>
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <div className="border-t border-neutral-200 p-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="input flex-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !sendingMessage) {
                      sendChatMessage();
                    }
                  }}
                />
                <button
                  onClick={sendChatMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="btn btn-primary px-4 disabled:bg-neutral-300 disabled:text-neutral-500"
                >
                  <FiSend className="h-4 w-4" />
                  <span className="hidden sm:inline">{sendingMessage ? "Sending..." : "Send"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 
